import React from 'react';
import { AlertCircle, RefreshCw, WifiOff, XCircle, Settings } from 'lucide-react';

const ERROR_TYPES = {
  network: {
    icon: WifiOff,
    title: 'Tidak Ada Koneksi',
    description: 'Periksa koneksi internet Anda dan coba lagi',
    color: 'from-orange-400 to-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  api: {
    icon: XCircle,
    title: 'Terjadi Kesalahan',
    description: 'Tidak dapat terhubung ke server AI',
    color: 'from-red-400 to-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  auth: {
    icon: Settings,
    title: 'API Key Tidak Valid',
    description: 'Periksa kembali API Key Anda di pengaturan',
    color: 'from-yellow-400 to-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  timeout: {
    icon: AlertCircle,
    title: 'Request Timeout',
    description: 'Permintaan memakan waktu terlalu lama, silakan coba lagi',
    color: 'from-blue-400 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  general: {
    icon: AlertCircle,
    title: 'Oops! Ada Masalah',
    description: 'Terjadi kesalahan yang tidak terduga',
    color: 'from-stone-400 to-stone-600',
    bgColor: 'bg-stone-50',
    borderColor: 'border-stone-200'
  }
};

export default function ErrorState({ 
  type = 'general',
  message,
  onRetry,
  onSettings,
  customTitle,
  customDescription
}) {
  const error = ERROR_TYPES[type] || ERROR_TYPES.general;
  const Icon = error.icon;

  return (
    <div className="max-w-4xl mx-auto px-6 mb-6 animate-fade-in">
      <div className={`${error.bgColor} border-2 ${error.borderColor} rounded-2xl p-8 shadow-md`}>
        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className={`w-20 h-20 bg-gradient-to-br ${error.color} rounded-2xl flex items-center justify-center mb-5 shadow-lg animate-shake`}>
            <Icon className="w-10 h-10 text-white" strokeWidth={2.5} />
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-stone-800 mb-2">
            {customTitle || error.title}
          </h3>

          {/* Description */}
          <p className="text-base text-stone-600 mb-2 max-w-md">
            {customDescription || error.description}
          </p>

          {/* Error Message (if provided) */}
          {message && (
            <div className="bg-white border border-stone-300 rounded-xl p-4 mb-6 max-w-md w-full">
              <p className="text-sm text-stone-700 font-mono text-left break-words">
                {message}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            {onRetry && (
              <button
                onClick={onRetry}
                className="modern-btn modern-btn-primary px-6 py-3"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Coba Lagi</span>
              </button>
            )}

            {onSettings && type === 'auth' && (
              <button
                onClick={onSettings}
                className="modern-btn modern-btn-secondary px-6 py-3"
              >
                <Settings className="w-5 h-5" />
                <span>Buka Pengaturan</span>
              </button>
            )}
          </div>

          {/* Help Text */}
          <p className="text-xs text-stone-500 mt-6">
            Jika masalah berlanjut, hubungi support
          </p>
        </div>
      </div>
    </div>
  );
}

// Preset Error States
export function NetworkError({ onRetry }) {
  return <ErrorState type="network" onRetry={onRetry} />;
}

export function APIError({ message, onRetry }) {
  return <ErrorState type="api" message={message} onRetry={onRetry} />;
}

export function AuthError({ onSettings }) {
  return <ErrorState type="auth" onSettings={onSettings} />;
}

export function TimeoutError({ onRetry }) {
  return <ErrorState type="timeout" onRetry={onRetry} />;
}